import { useEffect, useRef, useState } from 'react';

/**
 * Renders its children only once their place on the page comes within
 * `margin` of the viewport; until then, an empty box of `minHeight` holds the
 * space so nothing below it moves.
 *
 * React.lazy() alone fetches a chunk the moment the component renders — for a
 * widget far below the fold that is still during page load, competing with
 * the hero for bandwidth and the main thread. Wrapped in this, the map (and
 * Leaflet, and its dozens of tile images) waits until the visitor scrolls
 * towards it.
 *
 * `active={false}` holds off watching: while content above is still loading
 * the page is short, and the placeholder would count as "near" on arrival.
 */
export default function WhenVisible({ children, minHeight, margin = '600px', active = true }) {
    const ref = useRef(null);
    // No IntersectionObserver (very old browsers): render straight away.
    const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');

    useEffect(() => {
        if (seen || !active || !ref.current) return undefined;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setSeen(true);
                io.disconnect();
            }
        }, { rootMargin: margin });
        io.observe(ref.current);
        return () => io.disconnect();
    }, [seen, margin, active]);

    return seen ? children : <div ref={ref} style={{ minHeight }} />;
}
